import { useEffect } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  tabPersistenceAtom,
  initializeQueryTabsAtom,
  allQueryTabsAtom,
  cleanupQueryTabsAtom,
  saveTabLocallyAtom,
} from '@/atoms/query/queryAtoms';

// Subject to change as we add more features to the query tabs.
// This is a super basic implemetnation that handles the minimum viable
// persistence and cleanup.
export function useQueryTabs() {
  const initializeQueryTabs = useSetAtom(initializeQueryTabsAtom);
  const cleanupQueryTabs = useSetAtom(cleanupQueryTabsAtom);
  const allTabs = useAtomValue(allQueryTabsAtom);
  const saveTabLocally = useSetAtom(saveTabLocallyAtom);

  useEffect(() => {
    initializeQueryTabs();

    return () => {
      cleanupQueryTabs();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warn before closing/refreshing if there are unsaved changes locally. No API sync yet.
  useEffect(() => {
    const hasUnsavedChanges = allTabs.some((tab) => tab.isLocalDirty);

    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = () => {
      // Flush any locally-dirty tabs so the latest state is persisted
      allTabs.forEach((tab) => {
        if (tab.isLocalDirty) {
          saveTabLocally(tab.id);
        }
      });
      // No confirmation dialog – just save and allow the page to unload.
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [allTabs, saveTabLocally]);

  // Keep localStorage in sync by simply reading the persistence atom.
  // This leverages Jotai's reactivity to perform the write as a side-effect
  // whenever its dependencies (allTabsAtom, tabIdsAtom) change.
  // The persistence atom checks isInitialized internally to prevent
  // writing empty data on first render.
  useAtomValue(tabPersistenceAtom);
}
